import { uploadFile } from 'actions/admin';
import { setShowSuccess } from 'actions/ui';
import PageHeader from 'components/general/pageHeader';
import config from 'config';
import { ArrayHelpers, Help, SystemText } from '@salesperformanceint/e4shared';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { apiCode, apiConfig, apiFetch, apiGet, apiPost } from 'services';
import { e4Format, e4Formats } from 'helpers/dates';

export const UsersBulk = ({ history }) => {
    const dispatch = useDispatch();

    const [file, setFile] = React.useState(null);
    const [latest, setLatest] = React.useState({});
    const [errors, setErrors] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [refreshing, setRefreshing] = React.useState(false);
    const [disabled, setDisabled] = React.useState(true);

    const { user } = useSelector(state => state.user);

    const st = (path, ...rest) => SystemText(`Admin/UsersBulk/${path}`, ...rest);

    const getLatest = () => {
        setRefreshing(true);
        apiGet('bulk-latest?type=USERS', user.guid, response => {
            setTimeout(() => {
                setRefreshing(false);
                setLatest(response);
            }, 1000);
        });
    };
    React.useEffect(() => {
        getLatest();
    }, [user]);

    React.useEffect(() => {
        if (latest) {
            setDisabled(latest.status === 1);
            if (latest.status === 2) {
                const errorList = JSON.parse(latest.errors);
                if (errorList[0].item1) {
                    setErrors(errorList.map(err => st(`Upload/Errors/${err.item1}`, err.item2)));
                } else {
                    setErrors(errorList);
                }
            }
        } else {
            setDisabled(false);
        }
    }, [latest]);

    React.useEffect(() => {
        if (file) {
            file.companyId = user.company_Id;
            let errs = [];
            setErrors([]);
            if (file.name.indexOf('.xlsx') !== file.name.length - 5) {
                errs = [st('Upload/Errors/InvalidFileType')];
                setErrors(errs);
            } else {
                setLoading('U');
                const packet = {};
                const path = 'reports:users:upload';
                packet[path] = file;
                uploadFile(packet, path, config, apiConfig(user.guid, true), response => {
                    setTimeout(() => {
                        apiPost(
                            `bulk-upload?type=USERS&report=${response?.data?.path}`,
                            null,
                            user.guid,
                            () => {
                                setLoading(false);
                                setLatest({ status: 1, full_Name: user.full_Name, date_Uploaded: new Date() });
                                dispatch(setShowSuccess(st('Upload/Success')));
                            },
                            async err => {
                                var errorResponse = await err.response?.json();
                                setLoading(false);
                                setLatest({
                                    status: 2,
                                    full_Name: user.full_Name,
                                    date_Uploaded: new Date(),
                                    errors: JSON.stringify(errorResponse),
                                });
                            }
                        );
                    }, 1000);
                });
            }
        }
    }, [file]);

    const handleDownload = (type = 'D') => {
        setLoading(type);

        apiFetch(`${config.apiGateway.URL}bulk-users${apiCode(true)}type=${type}`, apiConfig(user.guid, null, true))
            .then(response => {
                const blob = response.blob();
                blob.type = response.headers['content-type'];
                return blob;
            })
            .then(blob => {
                setLoading(false);
                let now = new Date();
                let a = document.createElement('a');
                document.body.appendChild(a);
                let url = window.URL.createObjectURL(blob);
                a.style = 'display: none';
                a.href = url;
                a.download =
                    type === 'T'
                        ? 'richardson-insights-user-export-template.xlsx'
                        : `richardson-insights-user-export-${now.toISOString()}.xlsx`;
                a.click();
                window.URL.revokeObjectURL(url);
            })
            .catch(err => {
                // console.log(err);
            });
    };

    const extra = () => (
        <button
            id="btn-back"
            className="btn btn-info"
            onClick={() => {
                history.push('/admin/users');
            }}
        >
            <i className="fa fa-chevron-left mr10" />
            {st('Back')}
        </button>
    );

    return (
        <div data-testid="bulk-upload">
            <PageHeader title={st('Title')} extra={extra} adminMenu />
            <section className="content" data-testid="bulk-content">
                <div className="half-panel">
                    <div>
                        <div>
                            <h3>{st('Download/Title')}</h3>
                            <p>{st('Download/Summary')}</p>
                            <button
                                disabled={loading}
                                className="btn btn-primary"
                                data-testid="button-bulkdownload"
                                onClick={handleDownload}
                            >
                                <i className={`fa ${loading === 'D' ? 'fa-spinner fa-spin' : 'fa-cloud-download'} mr10`} />
                                {st('Download/Button')}
                            </button>
                            <button
                                disabled={loading}
                                className="btn  ml10"
                                data-testid="button-bulkdownload-template"
                                onClick={() => handleDownload('T')}
                            >
                                <i className={`fa ${loading === 'T' ? 'fa-spinner fa-spin' : 'fa-cloud-download'} mr10`} />
                                {st('DownloadTemplate/Button')}
                            </button>
                        </div>
                    </div>
                </div>
                <div className="half-panel">
                    <div>
                        <div>
                            <h3>{st('Upload/Title')}</h3>
                            <p>{st('Upload/Summary')}</p>
                            <label className="custom-file-upload btn btn-primary">
                                <input
                                    type="file"
                                    data-testid="file-bulkupload"
                                    onClick={e => {
                                        e.target.value = '';
                                        setFile(null);
                                    }}
                                    onChange={e => e.target.files && setFile(e.target.files[0])}
                                />
                                <i className={`fa ${loading === 'U' ? 'fa-spinner fa-spin' : 'fa-cloud-upload'} mr10`}></i>
                                {st('Upload/Button')}
                            </label>
                        </div>
                    </div>
                    {latest?.status && (
                        <div className="mt20">
                            <div>
                                <p className="bold">{st('Upload/Latest')}</p>
                                {latest.status === 1 && (
                                    <div>
                                        <i className="fa fa-user mr5" />
                                        {latest.full_Name} @ {e4Format(latest.date_Uploaded, e4Formats.long)}{' '}
                                        {e4Format(latest.date_Uploaded, e4Formats.time)}
                                        {refreshing ? (
                                            <label className="label label-info ml10">
                                                {st('Upload/Latest/Status/Refreshing')}
                                            </label>
                                        ) : (
                                            <label className="label label-primary ml10">
                                                {st('Upload/Latest/Status/Processing')}
                                            </label>
                                        )}
                                        <button className="btn-link" onClick={() => getLatest()}>
                                            <i
                                                className={`ml10 fa fa-refresh text-primary ${refreshing ? 'fa-spin' : ''}`}
                                            />
                                            <span className="ml10 text-primary">{st('Upload/Refresh')}</span>
                                        </button>
                                    </div>
                                )}
                                {latest.status === 2 && (
                                    <div>
                                        <i className="fa fa-user mr5" />
                                        {latest.full_Name} @ {e4Format(latest.date_Uploaded, e4Formats.long)}{' '}
                                        {e4Format(latest.date_Uploaded, e4Formats.time)}
                                        <label className="label label-danger ml10">{st('Upload/Latest/Status/Error')}</label>
                                        {ArrayHelpers.existsAndHasLength(errors) && (
                                            <Help
                                                id="upload-help"
                                                target={
                                                    <>
                                                        <i className="ml10 fa fa-warning text-danger" />
                                                        <span className="ml10 text-danger">{st('Upload/Errors')}</span>
                                                    </>
                                                }
                                            >
                                                {errors.slice(0, 10).map((err, i) => (
                                                    <div key={`error-${i}`}>{err}</div>
                                                ))}
                                                {errors.length > 10 && <div>+ {errors.length - 10} more</div>}
                                            </Help>
                                        )}
                                    </div>
                                )}
                                {latest.status === 3 && (
                                    <div>
                                        <i className="fa fa-user mr5" />
                                        {latest.full_Name} @ {e4Format(latest.date_Uploaded, e4Formats.long)}{' '}
                                        {e4Format(latest.date_Uploaded, e4Formats.time)}
                                        <label className="label label-success ml10">
                                            {st('Upload/Latest/Status/Success')}
                                        </label>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
};

export default UsersBulk;
